import React from 'react';
import {OrbitComponent} from '../base-components/orbit-component/orbit-component';
import {SingleGameRegularTemplateComponent} from './regular-template/single-game-regular-template.component';
import {GameInfo, OpenGameParameters} from '../../services/games/game-info';
import {CssClassNameBuilder} from '../../../utils/css-class-name-builder';
import {observer} from 'mobx-react';
import {GAME_BOX, SWIPER_MODE} from './css-constants';
import {SingleGameJackpotTemplateComponent} from './jackpot-template/single-game-jackpot-template.component';
import {JACKPOTS_ITEM, LIVE_CASINO_ITEM, LIVE_STREAMING_ITEM} from '../../themes/css-global-classes';
import {CyClass} from '../../../common/cy-classes';
import {SingleGameLiveTemplateComponent} from './live-template/single-game-live-template.component';
import {SingleGameLiveStreamingTemplate} from './live-streaming-template/single-game-live-streaming-template.component';

export interface SingleGameComponentProps {
    gameType: number;
    seoCategory: string;
    listTitle: string;
    listPosition?: number,
    AdminListID?: number,
    AIListTypeID?: number;
    templateType?: any;
    gameIndex?: number;
    skipIntermediatePage?: boolean;
    disableGameInfoHover: boolean;
    isInsideSwiper?: boolean;
    aiEngine?: string;
    useSquareIcons?: boolean;
    showNumberBadge?: boolean;
}

@observer
export class SingleGameComponent extends OrbitComponent<SingleGameComponentProps> {

    get gameInfo(): GameInfo {
        return this.services.games.gamesMap[this.props.gameType];
    }

    private openRealGame = () => {
        this._openGame(true);
    };

    private openAnonymousGame = () => {
        this._openGame(false);
    };

    private openInfoPage = () => {
        this.services.location.push(this.gameInfo.infoPageURL);
        this.sendAnalytics(`SEO_${this.gameInfo.gameType}`);
    };

    private _openGame(openInRealMode: boolean): void {


        const openGameParams: OpenGameParameters = {
            isRealMode: openInRealMode,
            pageName: this.props.seoCategory,
            listTitle: this.props.listTitle,
            listPosition: this.props.listPosition,
            AdminListID: this.props.AdminListID,
            AIListTypeID: this.props.AIListTypeID,
            gameIndex: this.props.gameIndex,
            aiEngine: this.props.aiEngine,
            showIntermediatePage: !this.props.skipIntermediatePage
        };

        this.gameInfo.open(openGameParams)
    }

    private get _allowPreview(): boolean {
        return this.services.lobbyGameInfoPreview.allowGamePreview
                && !this.props.disableGameInfoHover;
    }

    private sendAnalytics(label) {
        this.services.analytics.sendAnalytics(this.props.seoCategory, this.props.listTitle, label);
    }


    private onMouseEnter = () => {
        if (this._allowPreview) {
            this.services.lobbyGameInfoPreview.setGamePreview(this.props.gameType);
        }
    };


    private onMouseLeave = () => {
        if (this._allowPreview) {
            this.services.lobbyGameInfoPreview.clearGamePreview();
        }
    };


    private _buildComponentClasses(gameInfo: GameInfo): string {
        let builder = new CssClassNameBuilder();

        builder.append(GAME_BOX);

        if(this.props.isInsideSwiper) {
            builder = builder.append(SWIPER_MODE);
        }

        if (gameInfo) {
            builder = builder.append(`game-group-${toCssClass(gameInfo.gameGroup)}`)
                .append(`game-category-${gameInfo.gameCategory}`)
                .append(`game-company-${toCssClass(gameInfo.companyName)}`)
                .append(`game-type-${gameInfo.gameType}`);
        }

        if(this.props.skipIntermediatePage) {
            builder.append(CyClass('cy-skip-intermediate-page'));
        }

        return builder.build();

    }

    render() {

        let gameInfo = this.gameInfo;

        if(!gameInfo) {
            return null;
        }

        try {

            const classes = this._buildComponentClasses(gameInfo);

            switch (this.props.templateType) {

                case JACKPOTS_ITEM:
                    return (
                        <SingleGameJackpotTemplateComponent key={this.props.templateType}
                                                            gameInfo={gameInfo}
                                                            className={classes}
                                                            onOpenRealGame={this.openRealGame}
                        />
                    );

                case LIVE_CASINO_ITEM:
                    return (
                        <SingleGameLiveTemplateComponent key={this.props.templateType}
                                                         tableConfig={gameInfo}/>
                    );

                case LIVE_STREAMING_ITEM:
                    return (
                        <SingleGameLiveStreamingTemplate key={this.props.templateType}
                                                         tableConfig={gameInfo}
                                                         onOpenRealGame={this.openRealGame}
                        />
                    );

                default:
                    return (
                        <SingleGameRegularTemplateComponent key="SINGLE_GAME_REGULAR_TEMPLATE"
                            className={classes}
                            gameInfo={gameInfo}
                            disableGameInfoHover={this.props.disableGameInfoHover}
                            onMouseEnter={this.onMouseEnter}
                            onMouseLeave={this.onMouseLeave}
                            onOpenRealGame={this.openRealGame}
                            onOpenAnonymousGame={this.openAnonymousGame}
                            gameIndex={this.props.gameIndex}
                            showNumberBadge={this.props.showNumberBadge}
                            useSquareIcons={this.props.useSquareIcons}
                            onOpenInfoPage={this.openInfoPage}
                        />
                    );
            }

        } catch (err) {
            this.services.logger.error(`Failed to render SingleGameComponent for game ${gameInfo.gameType} - ${gameInfo.title} in list ${this.props.listTitle}`, err);
            return null;
        }
    }
}

function toCssClass(str){
    try{
        return str.replace(/\s+/g, '-').toLowerCase();
    }
    catch(e){
        return '';
    }
}

